import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import Title from "../components/ui/Title";
import Seperator from "../components/ui/Seperator";
import styled from "styled-components";
import { useEffect, useState } from "react";
import CustomDataTable from "../components/ui/CustomDataTable";
import { Card } from "../components/ui/Card";
import { disputeShipment, disputeShipmentPost } from "../config/api";
import { GridRowModel } from "@mui/x-data-grid";
import toast from "react-hot-toast";
import { create } from "zustand";
import { StyledButton } from "../components/ui/StyledButton";
import DatePickerEl from "../components/ui/DatePickerEl";
import dayjs, { Dayjs } from "dayjs";

interface syncState {
  syncWeight: number;
  setSyncWeight: () => void;
}

export const useSync = create<syncState>((set) => ({
  syncWeight: 0,
  setSyncWeight: () => set((state) => ({ syncWeight: state.syncWeight + 1 })),
}));

interface WeightDiscrepanciesData {
  shipment_counts: number;
  count_of_page: number;
  amount: number;
  shipments: ShipmentData[];
}

export interface ShipmentData {
  id: number;
  dispute_id: number;
  shipment_id: number;
  status: string;
  booked_weight: number;
  measured_weigth: number;
  weight_diff: number;
  booked_weight_amount: number;
  measured_weigth_amount: number;
  amount_diff: number;
  created: string;
  from_city: string;
  to_city: string;
  shipment_awb: string;
}

const weightColumns = [
  {
    field: "status",
    headerName: "Status",
    minWidth: 140,
    flex: 1,
  },
  {
    field: "weight",
    headerName: "Weight Difference",
    minWidth: 200,
    flex: 1,
    renderCell: (params: { row: GridRowModel }) => (
      <Stack>
        <Typography fontSize={"12px"}>
          Booked Weight: {params.row.booked_weight}
        </Typography>
        <Typography fontSize={"12px"}>
          Measured Weight: {params.row.measured_weigth}
        </Typography>
        <Typography fontSize={"12px"}>
          Weight Diff: {params.row.weight_diff}
        </Typography>
      </Stack>
    ),
  },

  {
    field: "booked_weight_amount",
    headerName: "Booked Weight Amount",
    minWidth: 200,
    flex: 1,
    renderCell: (params: { row: GridRowModel }) => (
      <Stack>
        <Typography fontSize={"12px"}>
          Booked Weight Amount: {params.row.booked_weight_amount}
        </Typography>
        <Typography fontSize={"12px"}>
          Measured Weight Amount: {params.row.measured_weigth_amount}
        </Typography>
        <Typography fontSize={"12px"}>
          Weight Diff Amount: {params.row.amount_diff}
        </Typography>
      </Stack>
    ),
  },

  {
    field: "shipment_awb",
    headerName: "AWB",
    minWidth: 200,
    flex: 1,
  },
  {
    field: "action",
    headerName: "Action",
    minWidth: 200,
    flex: 1,

    renderCell: (params: { row: GridRowModel }) => (
      <div>
        {params.row.status === "open" && (
          <RaiseDispute id={params.row.dispute_id} />
        )}
      </div>
    ),
  },
];

const RaiseDispute = ({ id }: { id: any }) => {
  const { setSyncWeight } = useSync();

  const onClickHandler = () => {
    disputeShipmentPost(id, "accepted")
      .then((data) => {
        setSyncWeight();
        toast.success("Dispute raised successfully");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div>
      <StyledButton onClick={onClickHandler} variant="contained">
        Raise
      </StyledButton>
    </div>
  );
};

const WeightInfo = styled.div`
  padding: 10px 20px;
  border-radius: 10px;
  border: 1px solid #e3e3e3;
`;

const WeightDiscrepancies = () => {
  const [weightData, setWeightData] = useState<WeightDiscrepanciesData>();

  const [startDate, setStartDate] = useState(dayjs().subtract(10, "days"));
  const [endDate, setEndDate] = useState(dayjs());

  const [status, setStatus] = useState("active");

  const { syncWeight } = useSync();

  useEffect(() => {
    fetchWeightDiscData();
  }, [status, syncWeight, startDate, endDate]);

  const fetchWeightDiscData = () => {
    disputeShipment(
      status,
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD")
    )
      .then((data) => {
        const rawData = data?.data?.data as WeightDiscrepanciesData;
        rawData?.shipments.map((el, index) => {
          el.id = index + 1;
        });
        setWeightData(rawData);
      })
      .catch((error) => {
        console.log(error);
        setWeightData({
          amount: 0,
          count_of_page: 0,
          shipments: [],
          shipment_counts: 0,
        });
      });
  };

  const handleDateChange = (value: Dayjs | null, type: "start" | "end") => {
    if (type === "start") setStartDate(value as Dayjs);
    if (type === "end") setEndDate(value as Dayjs);
  };

  return (
    <div>
      <Title title="Weight Discrepancies" />
      <Typography fontSize={"14px"}>
        Take action on your pending weight discrepancies, track weight disputes,
        and view history.
      </Typography>
      <Seperator />

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <WeightInfo>
            <Typography fontSize={"14px"}>
              Total Weight Discrepancies
            </Typography>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              marginTop={2}
            >
              <Typography fontSize={30}>0</Typography>
              <Typography fontSize={10}>Last 60 days</Typography>
            </Stack>
          </WeightInfo>
        </Grid>
        <Grid item xs={6} md={3}>
          <WeightInfo>
            <Typography fontSize={"14px"}>Discrepancies Accepted</Typography>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              marginTop={2}
            >
              <Typography fontSize={30}>0</Typography>
              <Typography fontSize={10}>Last 60 days</Typography>
            </Stack>
          </WeightInfo>
        </Grid>

        <Grid item xs={6} md={3}>
          <WeightInfo>
            <Typography fontSize={"14px"}>
              Disputes Accepted by Courier
            </Typography>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              marginTop={2}
            >
              <Typography fontSize={30}>0</Typography>
              <Typography fontSize={10}>Last 60 days</Typography>
            </Stack>
          </WeightInfo>
        </Grid>

        <Grid item xs={6} md={3}>
          <WeightInfo>
            <Typography fontSize={"14px"}>
              Disputes Rejected by Courier
            </Typography>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              marginTop={2}
            >
              <Typography fontSize={30}>0</Typography>
              <Typography fontSize={10}>Last 60 days</Typography>
            </Stack>
          </WeightInfo>
        </Grid>
      </Grid>

      <Card
        style={{
          marginTop: "40px",
        }}
      >
        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={"20px"}
        >
          <DatePickerEl
            defaultDate={startDate}
            handleChange={(time) => handleDateChange(time, "start")}
            label="From"
          />
          <DatePickerEl
            defaultDate={endDate}
            handleChange={(time) => handleDateChange(time, "end")}
            label="To"
          />
          <FormControl sx={{ minWidth: 200 }} size="small">
            <Select
              labelId="demo-select-small-label"
              id="demo-select-small"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value={"active"}>Active</MenuItem>
              <MenuItem value={"closed"}>Closed</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <CustomDataTable
          wantExcel={false}
          rows={weightData?.shipments ?? []}
          columns={weightColumns}
        />
      </Card>
    </div>
  );
};

export default WeightDiscrepancies;
